import { useEffect, useRef } from "react";
import { EditorState, StateEffect, StateField, type Extension, Transaction } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
} from "@codemirror/view";
import { defaultKeymap, history, historyKeymap, indentWithTab } from "@codemirror/commands";
import { HighlightStyle, syntaxHighlighting, indentUnit, bracketMatching } from "@codemirror/language";
import { javascript } from "@codemirror/lang-javascript";
import { linter, lintGutter } from "@codemirror/lint";
import { tags } from "@lezer/highlight";
import { syntaxDiagnostics } from "./syntax-check.ts";

// Reads the same `--tok-*` custom properties that statement code blocks use (see
// styles.css), so the editor and a snippet in the problem statement can never
// drift into two different colour schemes.
const highlight = HighlightStyle.define([
  { tag: tags.keyword, color: "var(--tok-keyword)" },
  { tag: [tags.string, tags.special(tags.string)], color: "var(--tok-string)" },
  { tag: [tags.number, tags.bool, tags.null], color: "var(--tok-number)" },
  { tag: [tags.function(tags.variableName), tags.definition(tags.variableName)], color: "var(--tok-fn)" },
  { tag: [tags.typeName, tags.className], color: "var(--tok-type)" },
  { tag: tags.comment, color: "var(--tok-comment)", fontStyle: "italic" },
  { tag: tags.operator, color: "var(--tok-operator)" },
  { tag: tags.propertyName, color: "var(--tok-property)" },
]);

const theme = EditorView.theme(
  {
    "&": { fontSize: "13.5px", height: "100%", backgroundColor: "transparent" },
    ".cm-scroller": {
      fontFamily: "ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace",
      lineHeight: "1.6",
    },
    ".cm-content": { caretColor: "#7fb6ff", padding: "12px 0" },
    ".cm-gutters": {
      backgroundColor: "transparent",
      border: "none",
      color: "#4a5262",
      paddingRight: "8px",
    },
    ".cm-activeLine": { backgroundColor: "rgba(255,255,255,0.03)" },
    ".cm-activeLineGutter": { backgroundColor: "transparent", color: "#8b94a6" },
    "&.cm-focused": { outline: "none" },
    ".cm-selectionBackground, ::selection": { backgroundColor: "#2c3b52 !important" },

    // Diagnostics. CodeMirror's default underline is a repeating SVG that reads
    // as grey noise on a dark background, so it is replaced with an explicit
    // wavy underline plus a faint wash, which stays legible against the code.
    ".cm-lintRange-error": {
      backgroundImage: "none",
      textDecoration: "underline wavy #f0645f",
      textDecorationSkipInk: "none",
      textUnderlineOffset: "3px",
      backgroundColor: "rgba(240, 100, 95, 0.10)",
    },
    ".cm-lint-marker-error": { content: "none", color: "#f0645f" },
    ".cm-gutter-lint": { width: "12px" },
    ".cm-tooltip.cm-tooltip-lint": {
      backgroundColor: "#1a1f2a",
      border: "1px solid #3a2422",
      borderRadius: "6px",
    },
    ".cm-diagnostic": {
      padding: "6px 10px",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSize: "12.5px",
    },
    ".cm-diagnostic-error": { borderLeft: "3px solid #f0645f" },
    // The line the failing run pointed at. Distinct from the lint underline: it
    // marks where to look, and survives until the next edit.
    ".cm-errorLine": { backgroundColor: "rgba(240, 100, 95, 0.13)" },
  },
  { dark: true },
);

// Marks the line a failed run pointed at. Separate from the lint underline: the
// underline says "this is broken", this says "this is the one that stopped the
// run". It clears on the next edit, because by then the offset is stale.
const setErrorLine = StateEffect.define<number | null>();

const errorLineField = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(decorations, tr) {
    for (const effect of tr.effects) {
      if (!effect.is(setErrorLine)) continue;
      if (effect.value == null) return Decoration.none;
      const at = Math.max(0, Math.min(effect.value, tr.state.doc.length));
      const line = tr.state.doc.lineAt(at);
      return Decoration.set([Decoration.line({ class: "cm-errorLine" }).range(line.from)]);
    }
    if (tr.docChanged) return Decoration.none;
    return decorations.map(tr.changes);
  },
  provide: (field) => EditorView.decorations.from(field),
});

type Props = {
  value: string;
  onChange: (value: string) => void;
  onRun: () => void;
  onPasteBlocked: () => void;
  readOnly?: boolean;
  /**
   * Where a failed run laid the blame, or null. Scrolls that line into view and
   * highlights it.
   *
   * An object rather than a bare offset on purpose: each run produces a fresh
   * one, so two consecutive failures at the same position still re-apply the
   * highlight. With a number, the second run would be an unchanged prop and the
   * mark would stay cleared by whatever edit came in between.
   */
  syntaxError?: { pos: number } | null;
};

/**
 * The editor. Two things here are not incidental.
 *
 * Paste is blocked, because the entire value of the drill is producing the code
 * from memory. It is blocked in two places: the DOM events (which covers the
 * keyboard, the context menu, drag-and-drop and middle-click paste on Linux) and
 * a transaction filter, which catches anything that reaches the document without
 * a DOM paste event. Neither stops a determined person with devtools, and they
 * are not meant to. They stop the reflex.
 *
 * Cmd/Ctrl+Enter runs the tests, and is registered ahead of the default keymap
 * so nothing else claims it.
 */
export function CodeEditor({
  value,
  onChange,
  onRun,
  onPasteBlocked,
  readOnly = false,
  syntaxError = null,
}: Props) {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView | null>(null);
  // Held in refs so changing a callback does not tear down and rebuild the
  // editor, which would lose the cursor and the undo history mid-attempt.
  const callbacks = useRef({ onChange, onRun, onPasteBlocked });
  callbacks.current = { onChange, onRun, onPasteBlocked };

  useEffect(() => {
    if (!host.current) return;

    const blockPaste = (event: Event) => {
      event.preventDefault();
      callbacks.current.onPasteBlocked();
      return true;
    };

    const extensions: Extension[] = [
      lineNumbers(),
      history(),
      bracketMatching(),
      highlightActiveLine(),
      indentUnit.of("  "),
      javascript({ typescript: true }),
      syntaxHighlighting(highlight),
      // Live syntax errors. The delay is what keeps this from parsing on every
      // keystroke, and it also stops the editor screaming red at half-typed code:
      // three quarters of a second of not typing is roughly when you have
      // finished the thought and a marker is useful rather than distracting.
      linter(syntaxDiagnostics, { delay: 750 }),
      lintGutter(),
      errorLineField,
      theme,
      keymap.of([
        {
          key: "Mod-Enter",
          preventDefault: true,
          run: () => {
            callbacks.current.onRun();
            return true;
          },
        },
        indentWithTab,
        ...defaultKeymap,
        ...historyKeymap,
      ]),
      EditorView.domEventHandlers({ paste: blockPaste, drop: blockPaste }),
      // Belt and braces: reject any transaction the editor itself labels as a
      // paste or a drop, in case one arrives without a DOM event we saw.
      EditorState.transactionFilter.of((tr) => {
        const event = tr.annotation(Transaction.userEvent);
        if (event === "input.paste" || event === "input.drop") {
          callbacks.current.onPasteBlocked();
          return [];
        }
        return tr;
      }),
      EditorView.updateListener.of((update) => {
        if (update.docChanged) callbacks.current.onChange(update.state.doc.toString());
      }),
      EditorState.readOnly.of(readOnly),
      EditorView.lineWrapping,
    ];

    const instance = new EditorView({
      state: EditorState.create({ doc: value, extensions }),
      parent: host.current,
    });
    view.current = instance;
    instance.focus();

    return () => {
      instance.destroy();
      view.current = null;
    };
    // Rebuilt only when the drill changes (the key prop) or read-only flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly]);

  // Accept an external reset (a new drill, or "reset to starter") without
  // rebuilding the editor, and without clobbering what is being typed.
  useEffect(() => {
    const instance = view.current;
    if (!instance) return;
    const current = instance.state.doc.toString();
    if (current === value) return;
    instance.dispatch({ changes: { from: 0, to: current.length, insert: value } });
  }, [value]);

  // Take the cursor to whatever a failed run blamed, so the message in the
  // results panel and the place to fix it are never more than a glance apart.
  useEffect(() => {
    const instance = view.current;
    if (!instance) return;
    if (syntaxError == null) {
      instance.dispatch({ effects: [setErrorLine.of(null)] });
      return;
    }
    const at = Math.max(0, Math.min(syntaxError.pos, instance.state.doc.length));
    instance.dispatch({
      selection: { anchor: at },
      effects: [setErrorLine.of(at), EditorView.scrollIntoView(at, { y: "center" })],
    });
    if (!readOnly) instance.focus();
  }, [syntaxError, readOnly]);

  return <div className="editor" ref={host} />;
}
